import { Injectable } from '@angular/core';

import { ProductV2, ProductV2Variation } from '../../models/product-v2.model';

import { CartItem } from './cart.state';

export interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

export interface CacheStats {
  hits: number;
  misses: number;
  size: number;
  hitRate: number;
}

@Injectable({
  providedIn: 'root'
})
export class CartCacheService {
  private readonly DEFAULT_TTL = 5 * 60 * 1000;
  private readonly MAX_CACHE_SIZE = 100;
  
  private itemCache = new Map<string, CacheEntry<CartItem>>();
  private productCache = new Map<string, CacheEntry<ProductV2>>();
  private variationCache = new Map<string, CacheEntry<ProductV2Variation>>();
  
  private stats = {
    hits: 0,
    misses: 0
  };

  cacheCartItem(item: CartItem, ttl: number = this.DEFAULT_TTL): void {
    if (!item || !item.id) return;
    
    this.itemCache.set(item.id, {
      data: { ...item },
      timestamp: Date.now(),
      ttl
    });
    
    this.enforceMaxSize();
  }

  getCachedCartItem(itemId: string): CartItem | null {
    const entry = this.itemCache.get(itemId);
    
    if (!entry) {
      this.stats.misses++;
      return null;
    }
    
    if (this.isExpired(entry)) {
      this.itemCache.delete(itemId);
      this.stats.misses++;
      return null;
    }
    
    this.stats.hits++;
    return { ...entry.data };
  }

  cacheProduct(product: ProductV2, ttl: number = this.DEFAULT_TTL): void {
    if (!product || (!product.uuid && !product.id)) return;
    
    const key = product.uuid || String(product.id);
    this.productCache.set(key, {
      data: { ...product },
      timestamp: Date.now(),
      ttl
    });
    
    this.enforceMaxSize();
  }

  getCachedProduct(productId: string): ProductV2 | null {
    const entry = this.productCache.get(productId);
    
    if (!entry) {
      this.stats.misses++;
      return null;
    }
    
    if (this.isExpired(entry)) {
      this.productCache.delete(productId);
      this.stats.misses++;
      return null;
    }
    
    this.stats.hits++;
    return { ...entry.data };
  }

  cacheVariation(variation: ProductV2Variation, ttl: number = this.DEFAULT_TTL): void {
    if (!variation || !variation.uuid) return;
    
    this.variationCache.set(variation.uuid, {
      data: { ...variation },
      timestamp: Date.now(),
      ttl
    });
    
    this.enforceMaxSize();
  }

  getCachedVariation(variationId: string): ProductV2Variation | null {
    const entry = this.variationCache.get(variationId);
    
    if (!entry) {
      this.stats.misses++;
      return null;
    }
    
    if (this.isExpired(entry)) {
      this.variationCache.delete(variationId);
      this.stats.misses++;
      return null;
    }
    
    this.stats.hits++;
    return { ...entry.data };
  }

  cacheCartItems(items: CartItem[], ttl: number = this.DEFAULT_TTL): void {
    items.forEach(item => this.cacheCartItem(item, ttl));
  }

  getCachedCartItems(itemIds: string[]): CartItem[] {
    return itemIds
      .map(id => this.getCachedCartItem(id))
      .filter((item): item is CartItem => item !== null);
  }

  hasCachedCartItem(itemId: string): boolean {
    const entry = this.itemCache.get(itemId);
    return entry ? !this.isExpired(entry) : false;
  }

  hasCachedProduct(productId: string): boolean {
    const entry = this.productCache.get(productId);
    return entry ? !this.isExpired(entry) : false;
  }

  hasCachedVariation(variationId: string): boolean {
    const entry = this.variationCache.get(variationId);
    return entry ? !this.isExpired(entry) : false;
  }

  removeCachedCartItem(itemId: string): void {
    this.itemCache.delete(itemId);
  }

  removeCachedProduct(productId: string): void {
    this.productCache.delete(productId);
  }

  removeCachedVariation(variationId: string): void {
    this.variationCache.delete(variationId);
  }

  clearCartItemCache(): void {
    this.itemCache.clear();
  }

  clearProductCache(): void {
    this.productCache.clear();
  }

  clearVariationCache(): void {
    this.variationCache.clear();
  }

  clearAllCaches(): void {
    this.clearCartItemCache();
    this.clearProductCache();
    this.clearVariationCache();
    this.resetStats();
  }

  cleanExpiredEntries(): void {
    this.cleanExpiredEntriesFromMap(this.itemCache);
    this.cleanExpiredEntriesFromMap(this.productCache);
    this.cleanExpiredEntriesFromMap(this.variationCache);
  }

  getCacheStats(): CacheStats {
    const totalRequests = this.stats.hits + this.stats.misses;
    const hitRate = totalRequests > 0 ? (this.stats.hits / totalRequests) * 100 : 0;
    
    return {
      hits: this.stats.hits,
      misses: this.stats.misses,
      size: this.itemCache.size + this.productCache.size + this.variationCache.size,
      hitRate: Math.round(hitRate * 100) / 100
    };
  }

  resetStats(): void {
    this.stats = { hits: 0, misses: 0 };
  }

  getCacheSize(): {
    cartItems: number;
    products: number;
    variations: number;
    total: number;
  } {
    return {
      cartItems: this.itemCache.size,
      products: this.productCache.size,
      variations: this.variationCache.size,
      total: this.itemCache.size + this.productCache.size + this.variationCache.size
    };
  }

  isCacheTooLarge(): boolean {
    const totalSize = this.itemCache.size + this.productCache.size + this.variationCache.size;
    return totalSize > this.MAX_CACHE_SIZE;
  }

  private isExpired<T>(entry: CacheEntry<T>): boolean {
    return Date.now() - entry.timestamp > entry.ttl;
  }

  private cleanExpiredEntriesFromMap<T>(map: Map<string, CacheEntry<T>>): void {
    const now = Date.now();
    for (const [key, entry] of map.entries()) {
      if (now - entry.timestamp > entry.ttl) {
        map.delete(key);
      }
    }
  }

  private enforceMaxSize(): void {
    if (this.isCacheTooLarge()) {
      this.removeOldestEntries(this.itemCache, Math.floor(this.MAX_CACHE_SIZE / 3));
      this.removeOldestEntries(this.productCache, Math.floor(this.MAX_CACHE_SIZE / 3));
      this.removeOldestEntries(this.variationCache, Math.floor(this.MAX_CACHE_SIZE / 3));
    }
  }

  private removeOldestEntries<T>(map: Map<string, CacheEntry<T>>, maxSize: number): void {
    if (map.size <= maxSize) return;
    
    const entries = Array.from(map.entries());
    entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
    
    const toRemove = entries.slice(0, entries.length - maxSize);
    toRemove.forEach(([key]) => map.delete(key));
  }

  preloadItems(items: CartItem[]): void {
    items.forEach(item => {
      this.cacheCartItem(item);
      this.cacheProduct(item.product);
      if (item.selectedVariation) {
        this.cacheVariation(item.selectedVariation);
      }
    });
  }

  warmUpCache(items: CartItem[]): void {
    this.clearAllCaches();
    this.preloadItems(items);
  }
}
